import { ROLE_ACCESS } from "src/consts/role";

import { PWF_ACTIVATION_STATUS } from "src/consts/accept-payment";
import { PAYMENT_METHOD } from "src/consts/transactions";
import { getSafely } from "src/helpers/object";
import { isValidRole } from "src/helpers/role";
import privateRoutes from "src/routes/private-routes";
import { isAllowedToAccessApMenus } from "src/scenes/AcceptPayment/utils/permission";
import { API_SETTINGS_COMPANY_IDS } from "src/scenes/Settings/consts/apiSetting";

import { RBAC_ID, RBAC_KEY } from "src/consts/rbac";
import { MENUS, SANDBOX_ONLY, VERIFICATION_ONLY } from "../consts/menus";
import { ACCEPT_PAYMENT_RBAC } from "../consts/permission-group-menu";

function getSpecialMenuSanboxConfigs(user) {
  const isShowCompany = user?.company?.id;

  return {
    settings: {
      condition: API_SETTINGS_COMPANY_IDS.includes(isShowCompany),
      to: privateRoutes.sandboxApiSettings.path
    }
  };
}

function overideMenu(menu, menuConfig) {
  const { condition, ...configs } = menuConfig;
  const newMenu = { ...menu };

  if (condition) {
    Object.keys(configs).forEach((confKey) => (newMenu[confKey] = configs[confKey]));
  }
  return newMenu;
}

export function overridePermissions(permissions, user, kycStatus, enableRefundMenu) {
  const pwfStatus = getSafely(["company", "pwf_status"], user);
  const paymentMethod = user?.company.payment_method?.code;
  let newPermissions = [...permissions];

  // const dummyKYCStatus = 5 // this kyc status not allowed to access ap menus
  // const dummyPwfStatus = 0 // this pwf status not allowed to access ap menus
  // condition to allow access Accept Payment child menus
  if (!isAllowedToAccessApMenus({ kycStatus, pwfStatus })) {
    const NOT_ALLOWED_AP_RBAC = ACCEPT_PAYMENT_RBAC.filter((key) => key !== RBAC_KEY.ACCEPT_PAYMENT_NEW);
    newPermissions = newPermissions.filter((permission) => !NOT_ALLOWED_AP_RBAC.includes(permission));
  }

  // "ID & Konfigurasi" is the Configuration page, which only an ACTIVE company has.
  if (pwfStatus !== PWF_ACTIVATION_STATUS.ACTIVE) {
    newPermissions = newPermissions.filter((permission) => permission !== RBAC_KEY.ACCEPT_PAYMENT_CONFIGURATION);
  }

  // Riwayat Refund is behind the Nexus flag `pay-refund-flags`. Subtracting the permission (rather
  // than filtering the menu array) is how every other conditional menu here works, so it takes
  // effect for both the parent-menu check and the childs filter below.
  if (!enableRefundMenu) {
    newPermissions = newPermissions.filter((permission) => permission !== RBAC_KEY.ACCEPT_PAYMENT_REFUND_HISTORY);
  }

  // this payment method not allowed to access document
  // condition to allow access Document menu
  if (paymentMethod !== PAYMENT_METHOD.CASH) {
    newPermissions = newPermissions.filter((permission) => permission !== RBAC_KEY.DOCUMENT);
  }

  return newPermissions;
}

export function getMenus(user, kycStatus, enableMenuAcceptPayment, enableRefundMenu) {
  const role = getSafely(["actions_rule", "message"], user);
  const ALL_MENUS = enableMenuAcceptPayment
    ? Object.values(MENUS)
    : Object.keys(MENUS)
        .filter((key) => key !== "accept_payment")
        .map((key) => MENUS[key]);

  if (isValidRole(role)) {
    // all permissions based on roles
    const permissions = Object.keys(RBAC_ID).filter((key) => ROLE_ACCESS[role].includes(RBAC_ID[key]));

    // override permissions based on specific condition that put on overridePermissions()
    const newPermissions = overridePermissions(permissions, user, kycStatus, enableRefundMenu);

    const accesibleParentMenu = ALL_MENUS.filter((menu) =>
      typeof menu.key === "string"
        ? newPermissions.includes(menu.key)
        : menu.key.some((key) => newPermissions.includes(key))
    );

    // Copy rather than assign into `menu`. MENUS is a module-level constant and Object.values()
    // hands back the very same objects, so `menu.childs = ...` used to edit the constant in place —
    // the narrowing was permanent and process-wide. That is invisible while a session's permissions
    // never change, but it silently breaks anything that can flip: Nexus resolves the refund flag
    // twice (anon `ffb`, then the company id), and the first `false` pass would delete the Riwayat
    // Refund child for good, so the later `true` pass had nothing left to re-include. Callers that
    // pass fewer arguments (Settings NavTabs) did the same damage to the sidebar's data.
    // A parent menu's `to` is a route PATH ("/transaction"), while newPermissions holds RBAC KEYS
    // ("mt_new_trx"), so comparing the two directly — as this did — was always false and the
    // override below always fired. RBAC_ID maps key -> path, so mapping the granted keys through it
    // compares paths with paths and lets the check mean what its comment says.
    //
    // This is behaviour-neutral for every menu and role today: each parent's `to` happens to equal
    // its first child's path, and the key granting that path is that child's own key, so both
    // expressions pick the same destination. It only starts to matter for a parent whose landing
    // page is not simply its first child.
    const permittedPaths = newPermissions.map((permission) => RBAC_ID[permission]);

    const accessibleMenu = accesibleParentMenu?.map((menu) => {
      if (!menu.childs) return menu;

      const newChilds = menu.childs.filter((child) => newPermissions.includes(child.key));

      return {
        ...menu,
        childs: newChilds,
        // Keep the parent's own destination when the role may open it; otherwise send it to the
        // first child the role can actually reach.
        to: permittedPaths.includes(menu.to) ? menu.to : newChilds[0]?.path[0]
      };
    });

    return accessibleMenu;
  }

  return [];
}

export function getOnlyVerification() {
  return [...Object.values(VERIFICATION_ONLY)];
}

export function getOnlySandbox(user, enableMenuAcceptPayment) {
  const specialMenus = getSpecialMenuSanboxConfigs(user);
  const specialMenuKeys = Object.keys(specialMenus);
  const menu = (
    enableMenuAcceptPayment
      ? Object.keys(SANDBOX_ONLY)
      : Object.keys(SANDBOX_ONLY).filter((key) => key !== "accept_payment")
  ).map((key) =>
    specialMenuKeys.includes(key) ? overideMenu(SANDBOX_ONLY[key], specialMenus[key]) : SANDBOX_ONLY[key]
  );

  return menu;
}
